import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Typography,
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Sheet,
  CircularProgress,
  Input,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { supabase } from "../../supabaseClient";
import { getInitials, fetchProfilePicture } from "../../utils";
import { useModal } from "../../context/ModalContext";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../Pagination";
import AddEditTeacherModal from "../modals/AddEditTeacherModal";

interface Teacher {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  profile_picture?: string | null;
  profile_picture_id: string | null;
  primary_classes: any[];
  secondary_classes: any[];
}

export default function TeachersTable() {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { openModal } = useModal();

  // Use pagination hook
  const {
    paginatedData: paginatedTeachers,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
  } = usePagination({
    data: teachers,
    searchTerm: searchQuery,
    searchFields: ["first_name", "last_name"],
  });

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const { data: teachersData, error } = await supabase
        .from("teachers")
        .select(`
          *,
          primary_classes:classes!classes_primary_teacher_id_fkey(
            id,
            start_month,
            start_year,
            end_month,
            end_year
          ),
          secondary_classes:classes!classes_secondary_teacher_id_fkey(
            id,
            start_month,
            start_year,
            end_month,
            end_year
          )
        `);

      if (error) throw error;

      // Transform the data to match our Teacher interface
      const transformedTeachers = await Promise.all(
        (teachersData || []).map(async (teacher) => {
          const profilePicture = await fetchProfilePicture("teachers", teacher.profile_picture_id);

          return {
            ...teacher,
            profile_picture: profilePicture,
          };
        })
      );

      setTeachers(transformedTeachers);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const handleAddTeacher = () => {
    openModal(
      <AddEditTeacherModal onTeacherUpdated={fetchTeachers} />
    );
  };

  const handleEditTeacher = (teacher: Teacher) => {
    openModal(
      <AddEditTeacherModal 
        teacher={teacher}
        onTeacherUpdated={fetchTeachers}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} gap={2}>
        <Input
          placeholder="Search teachers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startDecorator={<SearchIcon />}
          sx={{ width: 300 }}
        />
        <Button startDecorator={<AddIcon />} onClick={handleAddTeacher} sx={{ ml: 2, minWidth: 140 }}>
          Add Teacher
        </Button>
      </Box>
      <Sheet variant="outlined" sx={{ width: "100%", borderRadius: "sm", overflow: "auto", minHeight: 0 }}>
        {paginatedTeachers.length > 0 ? (
          <>
            <Table
              stickyHeader
              hoverRow
            >
              <thead>
                <tr>
                  <th style={{ width: 250 }}>Name</th>
                  <th style={{ width: 250 }}>Email</th>
                  <th style={{ width: 150 }}>Phone</th>
                  <th style={{ width: 200 }}>Primary Classes</th>
                  <th style={{ width: 200 }}>Secondary Classes</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTeachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={teacher.profile_picture || undefined}
                          alt={`${teacher.first_name} ${teacher.last_name}`}
                          sx={{ width: 32, height: 32 }}
                        >
                          {getInitials({
                            first_name: teacher.first_name,
                            last_name: teacher.last_name,
                          })}
                        </Avatar>
                        <Typography level="body-sm">
                          {teacher.first_name} {teacher.last_name}
                        </Typography>
                      </Box>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {teacher.email_address}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{teacher.phone_number}</Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {teacher.primary_classes.length > 0
                          ? teacher.primary_classes
                              .map((cls: any) => `${cls.start_month}/${cls.start_year} — ${cls.end_month}/${cls.end_year}`)
                              .join(", ")
                          : "—"}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {teacher.secondary_classes.length > 0
                          ? teacher.secondary_classes
                              .map((cls: any) => `${cls.start_month}/${cls.start_year} — ${cls.end_month}/${cls.end_year}`)
                              .join(", ")
                          : "—"}
                      </Typography>
                    </td>
                    <td>
                      <Tooltip title="Edit Teacher">
                        <IconButton
                          onClick={() => handleEditTeacher(teacher)}
                          variant="plain"
                          color="neutral"
                          size="sm"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              rowsPerPage={rowsPerPage}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>
              {searchQuery.trim() ? "No teachers found matching your search." : "No teachers found"}
            </Typography>
          </Box>
        )}
      </Sheet>
    </React.Fragment>
  );
}
